import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Loader2, CalendarRange } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { getArea } from "./shared";

const isoDate = (d) => d.toISOString().slice(0, 10);
const monthsAgo = (n) => { const d = new Date(); d.setMonth(d.getMonth() - n); return d; };

export default function ChartView({ studentId, colors = {} }) {
  const [tl, setTl] = useState(null);
  const [start, setStart] = useState(() => isoDate(monthsAgo(6)));
  const [end, setEnd] = useState(() => isoDate(new Date()));

  useEffect(() => {
    if (!studentId) return;
    setTl(null);
    api.get(`/parent/bep/timeline?student_id=${studentId}&start=${start}&end=${end}`)
      .then((r) => setTl(r.data)).catch(() => setTl({ areas: [], data: [] }));
  }, [studentId, start, end]);

  const hasData = tl && tl.data?.some((row) => tl.areas.some((a) => row[a] != null));

  return (
    <div data-testid="parent-chart">
      <h1 className="font-heading text-2xl font-bold text-slate-900">Gelişim Grafiği</h1>

      <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl bg-violet-50 px-3 py-2.5 text-sm font-medium text-violet-700">
        <CalendarRange className="h-4 w-4 flex-shrink-0" />
        <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="chart-date-start"
          className="rounded-lg border border-violet-200 bg-white px-2 py-1 text-slate-700 outline-none focus:border-violet-400" />
        <span className="text-violet-400">–</span>
        <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="chart-date-end"
          className="rounded-lg border border-violet-200 bg-white px-2 py-1 text-slate-700 outline-none focus:border-violet-400" />
      </div>

      <Card className="mt-4 rounded-2xl border-slate-200/60 p-4 pt-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
        {!tl ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>
        ) : !hasData ? (
          <p className="py-16 text-center text-sm text-slate-400">Seçili aralıkta henüz yeterli ilerleme kaydı yok.</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={tl.data} margin={{ top: 4, right: 12, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip />
              {tl.areas.map((a) => (
                <Line key={a} type="monotone" dataKey={a} stroke={colors[a] || getArea(a).hex} strokeWidth={2.5}
                  dot={{ r: 3, fill: colors[a] || getArea(a).hex }} connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card>

      {tl && tl.areas.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
          {tl.areas.map((a) => (
            <span key={a} className="inline-flex items-center gap-2 text-sm text-slate-600">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: colors[a] || getArea(a).hex }} />{a}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
